import { DestroyRef, Injectable } from '@angular/core';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environment';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { ParticipantModel } from '@shared/models/participant.model';
import {
  BlockRadioBox,
  BlockType,
  CheckboxItem,
  FeedbackColor,
  keyStrengthDescription,
  keyStrengthName,
  RadioItem,
  ViaSurveyStrength,
} from '@shared/models/step.model';
import { CourseContentService, ShortCodes } from '@app/courseContent/courseContent.service';
import {
  Answer,
  getViaSurveyEndpointUrl,
  PayloadLoginRegister,
  PayloadSubmitAnswer,
  VIA_SURVEY_ID,
  VIA_SURVEY_LANGUAGE,
  VIA_SURVEY_QUESTION_COUNT,
  ViaSurveyAnswerChoice,
  ViaSurveyEndpoint,
  ViaSurveyQuestion,
} from '@shared/models/viaSurvey.model';
import { ErrorService } from '@shared/services/error.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ViaSurveyService {
  private loginKey: string | null = null;
  private sessionKey: string | null = null;

  constructor(
    private courseContentService: CourseContentService,
    private errorService: ErrorService,
    private httpService: HttpService,
    private participantService: ParticipantService,
  ) {}

  private generateViaSurveyPasswordForParticipant(userId: number): string {
    return `${userId}!@#$%asdf248t`;
  }

  private getPayloadForRegisterAndLogin(): PayloadLoginRegister {
    const participant: ParticipantModel | null = this.participantService.participant();
    if (participant) {
      return {
        appKey: environment.viaSurveyApiKey,
        sendWelcomeEmailToUser: false,
        email: `${participant.id}`, // We use the user id here in case the user email changes in the future, the userID will never change
        firstName: 'Jeremy', // Random name to not send the real first name to via survey
        lastName: 'Suliver', // Random name to not send the real last name to via survey
        gender: participant.gender,
        password: this.generateViaSurveyPasswordForParticipant(participant.id),
      };
    }

    throw new Error(`No participant when registering user, not normal`);
  }

  public registerUser(destroyRef: DestroyRef): Promise<void> {
    return new Promise((resolve) => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.registerUser),
        body: this.getPayloadForRegisterAndLogin(),
      };

      this.httpService
        .request(requestOptions)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: (): void => {
            resolve();
          },
          error: (error: HttpErrorResponse) => {
            const ERROR_USER_REGISTERED_ALREADY: string =
              'You have already registered a user with this email address' as const;
            if (
              error.status === 500 &&
              (error.statusText === ERROR_USER_REGISTERED_ALREADY || error.error === ERROR_USER_REGISTERED_ALREADY)
            ) {
              resolve();
            } else {
              this.throwErrorAndNotifyBugsnag(ViaSurveyEndpoint.registerUser, error);
            }
          },
        });
    });
  }

  private throwErrorAndNotifyBugsnag(viaSurveyEndpoint: ViaSurveyEndpoint, error: Error): void {
    this.errorService.notifyBugsnag(error);
    console.error(`${viaSurveyEndpoint}: Unknown error returned by Via Survey: `, error);
    throw new Error(`${viaSurveyEndpoint}: Unknown error returned by Via Survey`);
  }

  public loginUser(destroyRef: DestroyRef): Promise<void> {
    return new Promise((resolve) => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.loginUser),
        body: this.getPayloadForRegisterAndLogin(),
      };

      this.httpService
        .request(requestOptions)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: (loginKey: string): void => {
            this.loginKey = loginKey;
            resolve();
          },
          error: (error: HttpErrorResponse) => {
            this.throwErrorAndNotifyBugsnag(ViaSurveyEndpoint.loginUser, error);
          },
        });
    });
  }

  public startSurvey(destroyRef: DestroyRef): Promise<void> {
    return new Promise((resolve) => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.startSurvey),
        body: {
          appKey: environment.viaSurveyApiKey,
          loginKey: this.loginKey,
          surveyID: VIA_SURVEY_ID,
          languageCode: VIA_SURVEY_LANGUAGE,
        },
      };

      this.httpService
        .request(requestOptions)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: (sessionKey: string): void => {
            this.sessionKey = sessionKey;
            resolve();
          },
          error: (error: HttpErrorResponse) => {
            this.throwErrorAndNotifyBugsnag(ViaSurveyEndpoint.startSurvey, error);
          },
        });
    });
  }

  public getAllViaSurveyQuestions(destroyRef: DestroyRef): Promise<ViaSurveyQuestion[]> {
    return new Promise((resolve) => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.getQuestions),
        body: {
          appKey: environment.viaSurveyApiKey,
          sessionkey: this.sessionKey,
          questionCount: VIA_SURVEY_QUESTION_COUNT,
        },
      };

      this.httpService
        .request(requestOptions)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe({
          next: (questions: ViaSurveyQuestion[]): void => {
            resolve(questions);
          },
          error: (error: HttpErrorResponse) => {
            this.throwErrorAndNotifyBugsnag(ViaSurveyEndpoint.getQuestions, error);
          },
        });
    });
  }

  public transformQuestionsToRadioBlock(questions: ViaSurveyQuestion[]): BlockRadioBox[] {
    // The list of questions returned by ViaSurvey are not in order (do you believe that), so it is necessary to sort
    const questionsSorted: ViaSurveyQuestion[] = questions.sort((a: ViaSurveyQuestion, b: ViaSurveyQuestion) => {
      return a.QuestionNumber - b.QuestionNumber;
    });

    return questionsSorted.map((question: ViaSurveyQuestion, index: number): BlockRadioBox => {
      const programDataCode: string = `via-survey-${question.QuestionID}`;
      const localStorageValue: string | null = this.courseContentService.getShortCodeSavedInLocalStorage(
        programDataCode,
      ) as string | null;
      return {
        element: BlockType.radiobox,
        source_program_data_code: null,
        id: question.QuestionID,
        program_data_code: programDataCode,
        data: {
          config: {
            required: true,
            minRadioBoxDisplayed: question.AnswerChoices.length,
          },
          label: `${question.QuestionNumber}: ${question.Text}`,
          subLabel: null,
          name: `${index}`,
          items: question.AnswerChoices.map((answer: ViaSurveyAnswerChoice): RadioItem => {
            const value: string = `${answer.ChoiceID}`;
            return {
              label: answer.Text,
              value,
              sub_label: null,
              selected: localStorageValue === value,
              feedback: null,
              color: FeedbackColor.green,
            };
          }),
          value: localStorageValue,
        },
      };
    });
  }

  public transformStrengthListToCheckboxItems(
    viaSurveyStrengths: ViaSurveyStrength[],
    selectedStrength: string[],
  ): CheckboxItem[] {
    return viaSurveyStrengths.map((strength: ViaSurveyStrength): CheckboxItem => {
      return {
        label: strength.StrengthName,
        sub_label: strength.StrengthDescription,
        value: strength.StrengthName,
        checked: selectedStrength.includes(strength.StrengthName),
      };
    });
  }

  public convertRadioBlocksToAnswers(radioBlocks: BlockRadioBox[]): Answer[] {
    return radioBlocks.map((radioBlock: BlockRadioBox): Answer => {
      if (radioBlock.data.value) {
        return {
          questionID: radioBlock.id,
          choiceID: +radioBlock.data.value,
        };
      }

      console.error(
        `submitAnswers() - for block`,
        radioBlock,
        '  the value is null or empty, this should never happen',
      );
      throw new Error(``);
    });
  }

  public submitAnswers(radioBlocks: BlockRadioBox[], destroyRef: DestroyRef): Promise<void> {
    return new Promise((resolve) => {
      if (this.loginKey && this.sessionKey) {
        const payload: PayloadSubmitAnswer = {
          appKey: environment.viaSurveyApiKey,
          loginKey: this.loginKey,
          sessionkey: this.sessionKey,
          answers: this.convertRadioBlocksToAnswers(radioBlocks),
        };

        const requestOptions: HttpQueryInterface = {
          type: HttpRequestType.POST,
          url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.submitAnswers),
          body: payload,
        };

        this.httpService
          .request(requestOptions)
          .pipe(takeUntilDestroyed(destroyRef))
          .subscribe({
            next: (allQuestionsSubmitted: boolean): void => {
              if (allQuestionsSubmitted) {
                resolve();
              } else {
                const errorMessage: string = `submitAnswers: api returns false, meaning not all questions have been submitted, it should never happen in our case`;
                console.error(errorMessage);
                const error: Error = new Error(errorMessage);
                this.errorService.notifyBugsnag(error);
                throw error;
              }
              resolve();
            },
            error: (error: HttpErrorResponse) => {
              this.throwErrorAndNotifyBugsnag(ViaSurveyEndpoint.submitAnswers, error);
            },
          });
      } else {
        throw new Error(
          `loginKey ${this.loginKey} or sessionKey (${this.sessionKey}) is null, this should never happen`,
        );
      }
    });
  }

  public getResults(destroyRef: DestroyRef): Promise<ViaSurveyStrength[]> {
    return new Promise((resolve) => {
      if (this.loginKey && this.sessionKey) {
        const requestOptions: HttpQueryInterface = {
          type: HttpRequestType.POST,
          url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.getResults),
          body: {
            appKey: environment.viaSurveyApiKey,
            loginKey: this.loginKey,
            sessionkey: this.sessionKey,
          },
        };

        this.httpService
          .request(requestOptions)
          .pipe(takeUntilDestroyed(destroyRef))
          .subscribe({
            next: (viaSurveyResults: ViaSurveyStrength[]): void => {
              const viaSurveyResultsFixed: ViaSurveyStrength[] = viaSurveyResults.map((strength: ViaSurveyStrength) => {
                let strengthDescription: string = strength.StrengthDescription;
                if (strengthDescription.startsWith('\r\n')) {
                  strengthDescription = strengthDescription.substring('\r\n'.length);
                }

                return {
                  ...strength,
                  StrengthDescription: strengthDescription,
                };
              });
              resolve(viaSurveyResultsFixed);
            },
            error: (error: HttpErrorResponse) => {
              this.throwErrorAndNotifyBugsnag(ViaSurveyEndpoint.getResults, error);
            },
          });
      } else {
        throw new Error(
          `loginKey ${this.loginKey} or sessionKey (${this.sessionKey}) is null, this should never happen`,
        );
      }
    });
  }

  public addViaSurveyStrengthShortCodeForPDFGeneration(viaSurveyStrengths: ViaSurveyStrength[]): void {
    const shortCodesToSaveForPdfGeneration: ShortCodes = {};
    viaSurveyStrengths.forEach((strength: ViaSurveyStrength, index: number): void => {
      const shortCodeKeyForStrengthName: string = `${keyStrengthName}_${index + 1}`;
      const shortCodeKeyForStrengthDescription: string = `${keyStrengthDescription}_${index + 1}`;

      shortCodesToSaveForPdfGeneration[shortCodeKeyForStrengthName] = strength[keyStrengthName];
      shortCodesToSaveForPdfGeneration[shortCodeKeyForStrengthDescription] = strength[keyStrengthDescription];
    });

    this.courseContentService.saveShortCodesInLocalStorage(shortCodesToSaveForPdfGeneration);
  }
}
